import { typographyTokens } from '../../theme/typographyTokens';
import { Box, Typography, Button, Drawer, Divider, Tabs, Tab, useTheme } from '@mui/material';
import { BannerCarousel } from '../../components/BannerCarousel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useEffect, useState } from 'react';
import SearchBar from '../../components/SearchBar/SearchBar';
import PageNavigation from '../../components/PageNavigation/PageNavigation';
import SepTableHeader from '../../components/SepTable/SepTableHeader';
import SepTableBody from '../../components/SepTable/SepTableBody';
import { useFindSepsQuery, useGetSubscribedSepsQuery, useUpdateSepSubscriptionsMutation } from '../../services/sepSlice';
import { useGetUserQuery } from '../../services/usersSlice';
import { HeadCell, headCells } from '../../components/SepTable/SepTableBody';
import ModalWidget from '../../components/ModalWidget/ModalWidget';
import EditColumnModal from '../../components/SepTable/EditColumnModal';
import SepFilterBar from '../../components/SepFilterBar/SepFilterBar';
import { SEPSearchRow, SEPStatus, SEPPhase, TableVisibleDataFields, ScreenName } from '../../../shared/types/SEP';
import { useGetDataFieldTemplatesQuery } from '../../services/dataFieldsSlice';
import SpinnerOverlay, { SpinnerSectionLoader } from '../../../frontend/components/Spinner/Spinner';
import { skipToken } from '@reduxjs/toolkit/query';

// Banner type for CarouselBanner
type Banner = { name: string; value: string };

// Interface for applied filters used internally (uses strings for date ranges)
interface AppliedFilters {
  sortBy: string;
  sortAsc: boolean;
  search: string;
  status: string;
  workFlowType: string;
  phase: string;
  keyTopics: string;
  createdAt: string;
  validationDate: string;
  completedAt: string;
  dataFieldColumns: string;
  filterApplied: boolean;
}

const AllSEPs = () => {
  // Key for sessionStorage
  const FILTERS_STORAGE_KEY = 'allSEPsFilters';
  const EDIT_COLUMNS_KEY = 'sharedSEPsEditColumns';

  // Reset only search
  const resetSearch = () => {
    setSearchText('');
    setSearchFilter('');
    setAppliedFilters((prev: any) => ({
      ...(prev || {}),
      search: '',
      filterApplied: prev?.filterApplied,
    }));
    setPage(0);
  };
  // Store applied filters for query
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [statusChecked, setStatusChecked] = useState<string[]>([]);
  const [phaseChecked, setPhaseChecked] = useState<string[]>([]);
  const [myToDosChecked, setMyToDosChecked] = useState<boolean>(false);
  const [showTodo, setShowTodo] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [updateSepSubs] = useUpdateSepSubscriptionsMutation();
  const [visibleColumns, setVisibleColumns] = useState<HeadCell[]>([
    ...headCells,
  ]);
  const [visibleDataFieldsList, setVisibleDataFieldsList] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [open, setOpen] = React.useState(false);
  const [workFlowChecked, setWorkFlowChecked] = useState<string[]>([]);
  const [keyTopicsChecked, setKeyTopicsChecked] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [startValidatedDate, setStartValidatedDate] = useState<Dayjs | null>(null);
  const [endValidatedDate, setEndValidatedDate] = useState<Dayjs | null>(null);
  const [startClosedDate, setStartClosedDate] = useState<Dayjs | null>(null);
  const [endClosedDate, setEndClosedDate] = useState<Dayjs | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isEditColumnApplied, setIsEditColumnApplied] = useState<boolean>(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  // Restore filters from sessionStorage on mount
  useEffect(() => {
    const savedFilters = typeof window !== 'undefined' ? sessionStorage.getItem(FILTERS_STORAGE_KEY) : null;
    const savedColumns = typeof window !== 'undefined' ? sessionStorage.getItem(EDIT_COLUMNS_KEY) : null;
    
    // Parse and restore visible columns if saved
    let parsedVisibleColumns: HeadCell[] | null = null;
    let restoredDataFieldColumns = '';
    
    if (savedColumns) {
      try {
        parsedVisibleColumns = JSON.parse(savedColumns);
        if (parsedVisibleColumns && Array.isArray(parsedVisibleColumns)) {
          setVisibleColumns(parsedVisibleColumns);
          const dataFieldsList = parsedVisibleColumns
            .filter((col: HeadCell) => col.visible && col.dbColumn && TableVisibleDataFields.includes(col.dbColumn))
            .map((col: HeadCell) => col.dbColumn)
            .filter((dbCol): dbCol is string => dbCol !== undefined);
          setVisibleDataFieldsList(dataFieldsList);
          
          // Use the same isDefault logic as in the visibleColumns useEffect for consistency
          const defaultVisibleColumns = headCells.filter(col => col.visible);
          const currentVisibleColumns = parsedVisibleColumns.filter((c: HeadCell) => c.visible);
          const isDefault = defaultVisibleColumns.length === currentVisibleColumns.length &&
            defaultVisibleColumns.every((col, index) => col.id === currentVisibleColumns[index]?.id);
          setIsEditColumnApplied(!isDefault);
          
          restoredDataFieldColumns = dataFieldsList.join(',');
        }
      } catch (e) {
        console.error('Error parsing saved columns from sessionStorage', e);
      }
    }
      
    if (savedFilters) {
      try {
        // Restore each filter state from the parsed object
        const parsedFilters = JSON.parse(savedFilters);
        if (parsedFilters) {
          setStatusChecked(parsedFilters.statusChecked || []); // Restore checked statuses
          setPhaseChecked(parsedFilters.phaseChecked || []);   // Restore checked phases
          setWorkFlowChecked(parsedFilters.workFlowChecked || []); // Restore checked workflow types
          setKeyTopicsChecked(parsedFilters.keyTopicsChecked || []); // Restore checked key topics
          setStartDate(parsedFilters.startDate ? dayjs(parsedFilters.startDate) : null); // Restore start date
          setEndDate(parsedFilters.endDate ? dayjs(parsedFilters.endDate) : null);       // Restore end date
          setStartValidatedDate(parsedFilters.startValidatedDate ? dayjs(parsedFilters.startValidatedDate) : null); // Restore validated start date
          setEndValidatedDate(parsedFilters.endValidatedDate ? dayjs(parsedFilters.endValidatedDate) : null);       // Restore validated end date
          setStartClosedDate(parsedFilters.startClosedDate ? dayjs(parsedFilters.startClosedDate) : null);          // Restore closed start date
          setEndClosedDate(parsedFilters.endClosedDate ? dayjs(parsedFilters.endClosedDate) : null);                // Restore closed end date

          // Use helper functions to format restored date and filter values
          const createdAt = getFormatedDates(parsedFilters.startDate ? dayjs(parsedFilters.startDate) : null, parsedFilters.endDate ? dayjs(parsedFilters.endDate) : null);
          const validationDate = getFormatedDates(parsedFilters.startValidatedDate ? dayjs(parsedFilters.startValidatedDate) : null, parsedFilters.endValidatedDate ? dayjs(parsedFilters.endValidatedDate) : null);
          const completedAt = getFormatedDates(parsedFilters.startClosedDate ? dayjs(parsedFilters.startClosedDate) : null, parsedFilters.endClosedDate ? dayjs(parsedFilters.endClosedDate) : null);

          // Build filter lists for query params using restored values
          // Only apply filters if at least one filter or date is set
          const filterStatusList = getStatusList(parsedFilters.statusChecked || []);
          const workFlowTypeList = getWorkFlowTypeList(parsedFilters.workFlowChecked || []);
          const keyTopicsList = getKeyTopicsList(parsedFilters.keyTopicsChecked || []);
          const phaseList = getFilterPhaseList(parsedFilters.phaseChecked || []);
          const filterAppliedStatus = filterStatusList || workFlowTypeList || phaseList || keyTopicsList || createdAt || validationDate || completedAt;
          if (filterAppliedStatus) {
            // Compose new filter object and update appliedFilters state
            const newFilters = {
              sortBy,
              sortAsc,
              search: searchFilter,
              status: filterStatusList,
              workFlowType: workFlowTypeList,
              phase: phaseList,
              keyTopics: keyTopicsList,
              createdAt,
              validationDate,
              completedAt,
              dataFieldColumns: restoredDataFieldColumns,
              filterApplied: true,
            };
            setAppliedFilters(newFilters);
          } else {
            // No filters applied but we have saved columns, trigger API with columns
            setAppliedFilters({
              sortBy,
              sortAsc,
              search: searchFilter,
              status: '',
              workFlowType: '',
              phase: '',
              keyTopics: '',
              createdAt: '',
              validationDate: '',
              completedAt: '',
              dataFieldColumns: restoredDataFieldColumns,
              filterApplied: false,
            });
          }
        }
      } catch (e) {
        // Log error if parsing fails
        console.error('Error parsing saved filters from sessionStorage', e);
      }
    } else if (parsedVisibleColumns) {
      // If no saved filters but we have visible columns, ensure we start with a clean slate but include columns
      const newFilters = {
        sortBy,
        sortAsc,
        search: searchFilter,
        status: '',
        workFlowType: '',
        phase: '',
        keyTopics: '',
        createdAt: '',
        validationDate: '',
        completedAt: '',
        dataFieldColumns: restoredDataFieldColumns,
        filterApplied: false,
      };
      setAppliedFilters(newFilters);
    } else {
      // No saved filters or columns - set default filters to ensure API is called
      setAppliedFilters({
        sortBy,
        sortAsc,
        search: '',
        status: '',
        workFlowType: '',
        phase: '',
        keyTopics: '',
        createdAt: '',
        validationDate: '',
        completedAt: '',
        dataFieldColumns: '',
        filterApplied: false,
      });
    }
    // Mark initialization as complete
    setIsInitialized(true);
  }, []);
  const { data: dataFieldTemplate } = useGetDataFieldTemplatesQuery();
  const workFlowData = dataFieldTemplate?.find((data: any) => data?.name === 'Workflow data field');
  const finalData: any = workFlowData?.dataFieldOptions?.map((val: any) => val.value);
  const keyTopicsData = dataFieldTemplate?.find((data: any) => data?.name === 'Key Topics');
  const keyTopicsOptions: any = keyTopicsData?.dataFieldOptions?.map((val: any) => val.value) || [];
  // (moved up for sessionStorage restore compatibility)
  const minStartDate = dayjs('2023-01-01');
  const minClosedDate = dayjs('2023-01-01');
  const maxClosedDate = dayjs();
  const minValidatedDate = dayjs('2023-01-01');
  const maxValidatedDate = dayjs();
  const maxEndDate = dayjs();

  useEffect(() => {
        // Only save to sessionStorage after initial mount/restore is complete
        if (isInitialized && visibleColumns && visibleColumns.length > 0) {
          const dataFieldColumns = visibleColumns
            .filter(col => col.visible && col.dbColumn && TableVisibleDataFields.includes(col.dbColumn))
            .map(col => col.dbColumn) as string[];
          setVisibleDataFieldsList(dataFieldColumns);
          
          // Compare current visible columns with default visible columns
          const defaultVisibleColumns = headCells.filter(col => col.visible);
          const currentVisibleColumns = visibleColumns.filter(c => c.visible);
          
          // Check if both arrays have the same length and same column IDs in the same order
          const isDefault = defaultVisibleColumns.length === currentVisibleColumns.length &&
            defaultVisibleColumns.every((col, index) => col.id === currentVisibleColumns[index]?.id);
          
          setIsEditColumnApplied(!isDefault);
          if(!isDefault){
            if (typeof window !== 'undefined') sessionStorage.setItem(EDIT_COLUMNS_KEY, JSON.stringify(visibleColumns));
          }else{
            if (typeof window !== 'undefined') sessionStorage.removeItem(EDIT_COLUMNS_KEY);
          }
          
          // Update appliedFilters with new dataFieldColumns to trigger API call
          setAppliedFilters((prev) => prev ? ({
            ...prev,
            dataFieldColumns: dataFieldColumns.join(','),
          }) : null);
        }
      }, [visibleColumns, isInitialized]);

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    if (newValue && endDate && newValue.isAfter(endDate)) {
      setEndDate(null); // Reset end date if it is before the new start date
    }
  };

  // Trigger API call when sortBy or sortAsc change
  useEffect(() => {
    if (isInitialized && (sortBy !== 'id' || sortAsc !== false)) {
      setAppliedFilters((prev) => prev ? ({
        ...prev,
        sortBy,
        sortAsc,
      }) : null);
      setPage(0);
    }
  }, [sortBy, sortAsc, isInitialized]);

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    if (newValue && startDate && newValue.isBefore(startDate)) {
      setStartDate(null); // Reset start date if it is after the new end date
    }
  };

  const statusArray: any = Object.values(SEPStatus).map((status) => status);

  const phaseArray: any = Object.values(SEPPhase).map((phase) => phase);
  const toggleDrawer = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const updateStatuses = (statuses: any) => {
    setStatusChecked(statuses);
    setPage(0);
  };

  const updatePhases = (phases: any) => {
    setPhaseChecked(phases);
    //setPage(0);
  };

  /**
   * Converts the checked status values into a comma-separated string for API query.
   * Handles special cases for certain phases (e.g., knockout, design, etc.).
   * @param statusChecked Array of checked status/phase values
   */
  const getStatusList = (statusChecked: any) => {
    const statusList = statusChecked
      .map((phase: any) => {
        // Map certain phases to 'active' for filtering
        if (
          phase === 'knockout' ||
          phase === 'design' ||
          phase === 'implement' ||
          phase === 'initiate'
        ) {
          return 'active';
        }
        // Map 'complete' phase to 'approved' status
        else if (phase === 'complete') {
          return 'approved';
        }
        // Only include valid statuses
        return statusArray.includes(phase) ? phase : null;
      })
      .filter((status: any) => status !== null);

    return statusList.map((status: any) => status.toString()).join(',')
  }

  const updateWorkFlowType = (type: any) => {
    setWorkFlowChecked(type)
  }

  const updateKeyTopics = (topics: any) => {
    setKeyTopicsChecked(topics)
  }

  /**
   * Converts checked workflow types into a comma-separated string for API query.
   * Only includes valid workflow types from finalData.
   * @param workFlowChecked Array of checked workflow types
   */
  const getWorkFlowTypeList = (workFlowChecked: any) => {
    const workFlowTypeList = workFlowChecked
      .map((workFlowType: any) => {
        return finalData?.includes(workFlowType) ? workFlowType : null;
      })
      .filter((status: any) => status !== null);

    return workFlowTypeList.map((status: any) => status.toString()).join(',')
  }

  /**
   * Converts checked key topics into a comma-separated string for API query.
   * Only includes valid key topics from keyTopicsOptions.
   * @param keyTopicsChecked Array of checked key topics
   */
  const getKeyTopicsList = (keyTopicsChecked: any) => {
    const keyTopicsList = keyTopicsChecked
      .map((keyTopic: any) => {
        return keyTopicsOptions?.includes(keyTopic) ? keyTopic : null;
      })
      .filter((topic: any) => topic !== null);

    return keyTopicsList.map((topic: any) => topic.toString()).join(',')
  }

  /**
   * Converts checked phase values into a comma-separated string for API query.
   * Only includes valid phases from phaseArray.
   * @param phaseChecked Array of checked phases
   */
  const getFilterPhaseList = (phaseChecked: any) => {
    const filterPhaseList = phaseChecked
      .map((phase: any) => {
        return phaseArray?.includes(phase) ? phase : null;
      })
      .filter((status: any) => status !== null);

    return filterPhaseList.map((status: any) => status.toString()).join(',')
  }

  /**
   * Formats a date range (start and end) as a comma-separated string for API query.
   * Returns an empty string if both dates are null.
   * @param startDate Start date (Dayjs or null)
   * @param endDate End date (Dayjs or null)
   */
  const getFormatedDates = (startDate: Dayjs | null, endDate: Dayjs | null) => {
    return [startDate ? startDate?.format('YYYY-MM-DD') : '', endDate ? endDate?.format('YYYY-MM-DD') : ''].filter(Boolean).join(',')
  }

  const createdAt: string = getFormatedDates(startDate, endDate);
  const completedAt: string = getFormatedDates(startClosedDate, endClosedDate);
  const validationDate: string = getFormatedDates(startValidatedDate, endValidatedDate);

  // Build query params from appliedFilters, always update limit/offset for pagination
  const queryParams = {
    ...(appliedFilters || {}),
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  };

  // Note: The API accepts createdAt/validationDate/completedAt as comma-separated strings
  // even though the SearchParams interface defines them as arrays
  const { data, isLoading: isLoadingSEPData, isFetching: isFetchingSEPData, isError: isErrorSEPData } = useFindSepsQuery(queryParams as any);
  const { data: loggedInUser, isLoading: isLoadingUser } = useGetUserQuery('me');
  const {
    data: subscribedSeps,
    isLoading: isLoadingSubscriptions,
    isError: isErrorSubscriptions,
    refetch: refetchSubscriptions,
  } = useGetSubscribedSepsQuery(loggedInUser?.email || skipToken);

  const subscriptionRows: SEPSearchRow[] = (subscribedSeps || []).map((sep) => ({
    ...sep,
    creator: sep.creator,
    tasks: sep.tasks,
    userNotesCount: 0,
  }));
  const pagedSubscriptionRows = subscriptionRows.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  const handleSubscriptionChange = async (sep: SEPSearchRow) => {
    const userEmail = loggedInUser?.email;
    if (!userEmail) return;

    const subscribers = (sep.subscribers || '')
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean);
    const isSubscribed = subscribers.includes(userEmail);
    const updatedSubscribers = isSubscribed
      ? subscribers.filter((email) => email !== userEmail)
      : [...subscribers, userEmail];

    await updateSepSubs({
      id: sep.id,
      body: {
        subscribers: updatedSubscribers.join(','),
        action: isSubscribed ? 'unsubscribed' : 'subscribed',
      } as any,
    });
    await refetchSubscriptions();
  };

  useEffect(() => {
    // Debounce our search function here so we can wait 500ms for the user to stop typing
    const delayDebounceFn = setTimeout(() => {
      if (searchText.length >= 3) {
        setSearchFilter(searchText);
        // Also trigger API call by updating appliedFilters
        setAppliedFilters((prev: any) => ({
          ...(prev || {}),
          sortBy,
          sortAsc,
          search: searchText,
          status: getStatusList(statusChecked),
          workFlowType: getWorkFlowTypeList(workFlowChecked),
          phase: getFilterPhaseList(phaseChecked),
          keyTopics: getKeyTopicsList(keyTopicsChecked),
          createdAt,
          validationDate,
          completedAt,
          dataFieldColumns: visibleDataFieldsList.join(','),
          filterApplied: prev?.filterApplied,
        }));
        setPage(0);
      } else {
        setSearchFilter('');
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchText]); // Only re-call effect if searchText changes
  const DrawerList = (
    <Box sx={{ width: 'auto', padding: '12px' }} role="presentation" >
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          component="span"
          sx={{
            fontSize: typographyTokens.fontSizes.xl,
            fontWeight: typographyTokens.fontWeights.bold,
            color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
            padding: '0'
          }}
        >
          Filters
        </Typography>
        <Button onClick={() => {
          // setName('')
          // setTaskID('')
          // setDepartmentID('')
          setOpen(false)
        }}
        > <i style={{ color: 'gray' }} className="fa fa-close" ></i></Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <SepFilterBar
        statusChecked={statusChecked}
        setStatusChecked={updateStatuses}
        showTodo={showTodo}
        setShowTodo={setShowTodo}
        myToDosChecked={myToDosChecked}
        setMyToDosChecked={setMyToDosChecked}
        data={data}
        filterPage={'allsep'}
        workFlowChecked={workFlowChecked}
        setWorkFlowChecked={updateWorkFlowType}
        phaseChecked={phaseChecked}
        setphaseChecked={updatePhases}
        finalData={finalData}
        keyTopicsChecked={keyTopicsChecked}
        setKeyTopicsChecked={updateKeyTopics}
        keyTopicsOptions={keyTopicsOptions}
      />
      <Box
        sx={{
          mt: 2,
          p: 2,
          backgroundColor: theme.palette.content.white,
          border: '1px solid',
          borderColor: theme.palette.supportingAdditional.tealContainerStroke,
          borderRadius: '6px',
          color: theme.palette.content.default,
          '& .MuiTypography-root, & .MuiInputBase-input, & .MuiFormLabel-root': {
            color: theme.palette.content.default,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.supportingAdditional.tealContainerStroke,
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.prime.blue,
          },
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: typographyTokens.fontSizes.body,
            fontWeight: typographyTokens.fontWeights.bold,
            p: 1,
            color: theme.palette.content.default,
          }}
        >View by created date range:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker", "DatePicker"]}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              pb: 1
            }}>
              <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }} >
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  minDate={minStartDate}
                  maxDate={endDate || maxEndDate}
                />
                {startDate && (
                  <Button size="small" sx={{ minWidth: 0, ml: 1 }} onClick={() => setStartDate(null)}>
                    <i className="fa fa-close" style={{ color: 'gray' }}></i>
                  </Button>
                )}
              </Box>
              <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate || minStartDate}
                  maxDate={maxEndDate}
                />
                {endDate && (
                  <Button size="small" sx={{ minWidth: 0, ml: 1 }} onClick={() => setEndDate(null)}>
                    <i className="fa fa-close" style={{ color: 'gray' }}></i>
                  </Button>
                )}
              </Box>
            </Box>
          </DemoContainer>
        </LocalizationProvider>

        <Divider sx={{ marginY: "8px" }} />

        <Typography
          component="div"
          sx={{
            fontSize: typographyTokens.fontSizes.body,
            fontWeight: typographyTokens.fontWeights.bold,
            p: 1,
            color: theme.palette.content.default,
          }}
        >View by validated date range:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker", "DatePicker"]}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              pb: 1
            }}>
              <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }} >
                <DatePicker
                  label="Start Validated Date"
                  value={startValidatedDate}
                  onChange={setStartValidatedDate}
                  minDate={minValidatedDate}
                  maxDate={endValidatedDate || maxValidatedDate}
                />
                {startValidatedDate && (
                  <Button size="small" sx={{ minWidth: 0, ml: 1 }} onClick={() => setStartValidatedDate(null)}>
                    <i className="fa fa-close" style={{ color: 'gray' }}></i>
                  </Button>
                )}
              </Box>
              <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  label="End Validated Date"
                  value={endValidatedDate}
                  onChange={setEndValidatedDate}
                  minDate={startValidatedDate || minValidatedDate}
                  maxDate={maxValidatedDate}
                />
                {endValidatedDate && (
                  <Button size="small" sx={{ minWidth: 0, ml: 1 }} onClick={() => setEndValidatedDate(null)}>
                    <i className="fa fa-close" style={{ color: 'gray' }}></i>
                  </Button>
                )}
              </Box>
            </Box>
          </DemoContainer>
        </LocalizationProvider>

        <Divider sx={{ marginY: "8px" }} />

        <Typography
          component="div"
          sx={{
            fontSize: typographyTokens.fontSizes.body,
            fontWeight: typographyTokens.fontWeights.bold,
            p: 1,
            color: theme.palette.content.default,
          }}
        >View by closed date range:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker", "DatePicker"]}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }} >
                <DatePicker
                  label="Start Closed Date"
                  value={startClosedDate}
                  onChange={setStartClosedDate}
                  minDate={minClosedDate}
                  maxDate={endClosedDate || maxClosedDate}
                />
                {startClosedDate && (
                  <Button size="small" sx={{ minWidth: 0, ml: 1 }} onClick={() => setStartClosedDate(null)}>
                    <i className="fa fa-close" style={{ color: 'gray' }}></i>
                  </Button>
                )}
              </Box>
              <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  label="End Closed Date"
                  value={endClosedDate}
                  onChange={setEndClosedDate}
                  minDate={startClosedDate || minClosedDate}
                  maxDate={maxClosedDate}
                />
                {endClosedDate && (
                  <Button size="small" sx={{ minWidth: 0, ml: 1 }} onClick={() => setEndClosedDate(null)}>
                    <i className="fa fa-close" style={{ color: 'gray' }}></i>
                  </Button>
                )}
              </Box>
            </Box>
          </DemoContainer>
        </LocalizationProvider>
      </Box>
      <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-end", px: 2, gap: 2 }}>
        <Button
          size="large"
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: '#2372B9',
            font: 'normal normal 600 16px/22px Open Sans',
          }}
          onClick={() => {
            // Build applied filters from current state, including date filters
            const createdAt = getFormatedDates(startDate, endDate);
            const validationDate = getFormatedDates(startValidatedDate, endValidatedDate);
            const completedAt = getFormatedDates(startClosedDate, endClosedDate);
            const filterStatusList = getStatusList(statusChecked);
            const workFlowTypeList = getWorkFlowTypeList(workFlowChecked);
            const phaseList = getFilterPhaseList(phaseChecked);
            const keyTopicsList = getKeyTopicsList(keyTopicsChecked);
            const filterAppliedStatus = filterStatusList || workFlowTypeList || phaseList || keyTopicsList || createdAt || validationDate || completedAt;
            if (filterAppliedStatus) {
              const newFilters = {
                ...appliedFilters,
                sortBy,
                sortAsc,
                search: searchFilter,
                status: filterStatusList,
                workFlowType: workFlowTypeList,
                phase: phaseList,
                keyTopics: keyTopicsList,
                createdAt,
                validationDate,
                completedAt,
                dataFieldColumns: visibleDataFieldsList.join(','),
                filterApplied: filterAppliedStatus ? true : false,
              };

              setAppliedFilters(newFilters);
              // Save all relevant filter state to sessionStorage
              if (typeof window !== 'undefined') sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
                statusChecked,
                phaseChecked,
                workFlowChecked,
                keyTopicsChecked,
                startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
                endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
                startValidatedDate: startValidatedDate ? startValidatedDate.format('YYYY-MM-DD') : null,
                endValidatedDate: endValidatedDate ? endValidatedDate.format('YYYY-MM-DD') : null,
                startClosedDate: startClosedDate ? startClosedDate.format('YYYY-MM-DD') : null,
                endClosedDate: endClosedDate ? endClosedDate.format('YYYY-MM-DD') : null,
              }));
              setPage(0);
              setOpen(false);
            }

          }}
        >
          Apply
        </Button>
        <Button
          size="large"
          variant="outlined"
          sx={{
            textTransform: 'none',
            font: 'normal normal 600 16px/22px Open Sans',
          }}
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            setStartValidatedDate(null);
            setEndValidatedDate(null);
            setStartClosedDate(null);
            setEndClosedDate(null);
            setStatusChecked([]);
            setPhaseChecked([]);
            setWorkFlowChecked([]);
            setKeyTopicsChecked([]);
            setAppliedFilters(() => ({
              sortBy,
              sortAsc,
              search: searchFilter,
              status: '',
              workFlowType: '',
              phase: '',
              keyTopics: '',
              createdAt: '',
              validationDate: '',
              completedAt: '',
              dataFieldColumns: visibleDataFieldsList.join(','),
              filterApplied: false,
            }));
            setPage(0);
            setOpen(false);
            if (typeof window !== 'undefined') sessionStorage.removeItem(FILTERS_STORAGE_KEY);
          }}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
  return (
    <Box display="flex" flexDirection="column" flexGrow={1} minHeight={0} height="100%" overflow="hidden" pt="24px">
      <ModalWidget
        isOpen={isOpen}
        title="Edit Columns"
        children={
          <EditColumnModal
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            closeModal={() => setIsOpen(false)}
            defaultColumns={[...headCells]}
            isModalOpen={isOpen}
          />
        }
        onClose={() => setIsOpen(false)}
        backgroundColor={isDarkMode ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight}
        textColor={isDarkMode ? theme.palette.text.primary : theme.palette.content.default}
      />
      <SearchBar
        title="All SEPs"
        searchText={searchText}
        setSearchText={setSearchText}
        reset={resetSearch}
      />
      
      <Drawer
        open={open}
        PaperProps={{ sx: { backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight } }}
      >{DrawerList}
      </Drawer>

      <BannerCarousel />

      {((activeTab === 0 && isLoadingSEPData) || (activeTab === 1 && (isLoadingUser || isLoadingSubscriptions))) ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexGrow={1}
          py="24px"
        >
          <SpinnerOverlay loaderText='' showSpinner={false} />
          <SpinnerSectionLoader
            loaderText="Loading SEPs..."
            showSpinner={true} />
        </Box>
      ) : (activeTab === 0 && isErrorSEPData) || (activeTab === 1 && isErrorSubscriptions) ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexGrow={1}
          py="24px"
        >
          <Typography color="error.main" fontSize={typographyTokens.fontSizes.h5}>
            Error
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
          <SepTableHeader
            count={activeTab === 0 ? data?.count ?? 0 : subscriptionRows.length}
            sortBy={sortBy}
            sortAsc={sortAsc}
            status={statusChecked
              .map((phase: any) => phase.toString())
              .join(',')}
            searchFilter={searchFilter}
            resultNumber={activeTab === 0 ? data?.count ?? 0 : subscriptionRows.length}
            onModalOpen={() => setIsOpen(true)}
            loading={activeTab === 0 ? isFetchingSEPData : isLoadingSubscriptions}
            isEditColumnApplied={isEditColumnApplied}
            toggleDrawer={toggleDrawer}
            appliedFilters={appliedFilters}
            hideFilterButton={activeTab === 1}
            hideEditColumn={activeTab === 1}
            tabs={
              <Tabs
                value={activeTab}
                onChange={(_, value) => {
                  setActiveTab(value);
                  setPage(0);
                }}
                aria-label="SEP request tabs"
                TabIndicatorProps={{ sx: { display: 'none' } }}
                sx={{
                  color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
                  minHeight: 'auto',
                  '& .MuiTab-root': {
                    minHeight: 'auto',
                    px: 2,
                    py: '6px',
                    border: '1px solid',
                    borderColor: theme.palette.supportingAdditional.tealContainerStroke,
                    borderRadius: 0,
                    color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
                    textTransform: 'uppercase',
                  },
                  '& .MuiTab-root:not(:first-of-type)': {
                    marginLeft: '-1px',
                  },
                  '& .MuiTab-root:first-of-type': {
                    borderRadius: '6px 0 0 6px',
                  },
                  '& .MuiTab-root:last-of-type': {
                    borderRadius: '0 6px 6px 0',
                  },
                  '& .MuiTab-root.Mui-selected': {
                    zIndex: 1,
                    borderColor: theme.palette.prime.orange,
                    backgroundColor: theme.palette.prime.orange,
                    color: theme.palette.content.white,
                  },
                }}
              >
                <Tab label="All Requests" />
                <Tab label="My Subscriptions" />
              </Tabs>
            }
          />
          {(activeTab === 0 && isFetchingSEPData) ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexGrow={1}
              py="24px"
            >
              <SpinnerOverlay loaderText='' showSpinner={false} />
              <SpinnerSectionLoader
                loaderText="Loading SEPs..."
                showSpinner={true} />
            </Box>
          ) : activeTab === 0 && isErrorSEPData ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexGrow={1}
              py="24px"
            >
              <Typography color="error.main" fontSize={typographyTokens.fontSizes.h5}>
                Error
              </Typography>
            </Box>
          ) : (
            <SepTableBody
              rows={activeTab === 0 ? data?.seps || [] : pagedSubscriptionRows}
              count={activeTab === 0 ? data?.count ?? 0 : subscriptionRows.length}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortAsc={sortAsc}
              setSortAsc={setSortAsc}
              headers={[...(activeTab === 0 ? visibleColumns : headCells.filter(col => col.visible)),
                { id: 'subscription', key: '', label: 'Subscription', visible: true },
              ]}
              loggedInUser={activeTab === 0 ? '' : ''}
              currentUserEmail={loggedInUser?.email}
              screenName={ScreenName.allSEP}
              onSubscriptionChange={handleSubscriptionChange}
            />
          )}
          <PageNavigation
              count={activeTab === 0 ? data?.count ?? 0 : subscriptionRows.length}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
        </Box>
      )}
    </Box>
  );
};

export default AllSEPs;


